/**
 * useDexLiveScanQuickStart — b17m (rebased); singleton-backed 2026-07.
 *
 * Thin wrapper that exposes a single `quickStart()` async function for the
 * HeroDexButton flat long-press menu.
 *
 * It no longer instantiates its own `useDexLiveScan`. Every scan surface now
 * shares ONE session via `LiveScanProvider` (see that file for the why — the
 * per-component instances were the root of the "navigation fires a second
 * broadcast" regression). This hook reads that shared session and only drives
 * the START path; the variant fallback is owned by the provider, which tracks
 * the active pill, so this hook no longer resolves a variant itself.
 *
 * Responsibilities:
 *   - `quickStart()` runs ONLY the start path:
 *       - no-ops if phase is "starting" or "finishing" (already in flight)
 *       - stops first if phase is "saved" or "error", then starts fresh
 *       - starts the scan when phase is "idle"
 *       - no-ops if phase is "scanning" (user stops via the existing menu)
 *       - surfaces a destructive toast on error ("Live Scan unavailable")
 *   - Does NOT touch the finish/conflict/review path — that stays in
 *     DexLiveScanMenu.
 */
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLiveScan } from "@/components/LiveScanProvider";

export function useDexLiveScanQuickStart() {
  const { toast } = useToast();
  const liveScan = useLiveScan();

  const quickStart = useCallback(async () => {
    const phase = liveScan.state.phase;
    // Already in flight — ignore duplicate taps.
    if (phase === "starting" || phase === "finishing") return;
    // User is scanning — they stop via the existing DexLiveScanMenu.
    if (phase === "scanning") return;
    // Terminal phases: tear down the previous session before re-starting.
    if (phase === "saved" || phase === "error") {
      // A live broadcast is kept for start() to adopt (see stop()).
      await liveScan.stop({ keepBroadcast: true });
    }
    // Start the scan (idle path, or after stop above).
    try {
      await liveScan.start("hero-quick-start");
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Try again in the iOS app.";
      toast({
        title: "Live Scan unavailable",
        description,
        variant: "destructive",
      });
    }
  }, [liveScan, toast]);

  return { quickStart };
}
